/**
 * Atualização da app instalada (PWA).
 *
 * O defeito que isto resolve: o `sw.js` chamava `skipWaiting()` ao instalar, por isso a versão nova
 * assumia o controlo em silêncio, mas a página já aberta continuava a correr o JavaScript antigo — a
 * versão nova só aparecia depois de fechar e reabrir a app (duas vezes), e no telemóvel ficava presa
 * na versão em cache. Agora o worker novo fica em espera e a app mostra a faixa
 * `components/Pwa/UpdateBanner.jsx` («Nova versão do GyMag disponível» / «Atualizar» / «Depois»).
 *
 * Aplicar a versão é recarregar a página: o token da sessão vive no localStorage e NADA aqui lhe
 * toca — quem tem sessão iniciada continua com ela depois de atualizar.
 *
 * Este módulo não desenha nada. Liga o registo do service worker aos eventos (`updatefound` e
 * `statechange` → `waiting`), verifica se há versão nova e aplica-a a pedido.
 */

/** De 30 em 30 minutos (e ao arrancar e ao voltar à app) — nunca num ciclo apertado. */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/** Duas verificações seguidas (focus + visibilitychange da mesma volta) contam como uma. */
const MIN_MS_BETWEEN_CHECKS = 30 * 1000;

/** Onde uma atualização nunca aparece: a meio de um treino, um recarregamento perdia as séries no ecrã. */
const WORKOUT_ROUTE = /^\/(treino|workout)(\/|$)/;

export function isWorkoutRoute(pathname) {
  return WORKOUT_ROUTE.test(String(pathname || ''));
}

let registration = null;
let waiting = null;
let applied = false;
let stopChecks = null;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener(waiting);
    } catch (e) {
      // Um ecrã que rebenta ao ser avisado não pode impedir os outros de receber o aviso.
    }
  });
}

/** O worker novo que está à espera de entrar (null quando não há versão nova). */
export function getWaitingWorker() {
  return waiting;
}

/** Avisa quando aparece (ou desaparece) uma versão nova. Recebe o worker em espera; devolve o cancelamento. */
export function subscribeToUpdate(listener) {
  listeners.add(listener);
  listener(waiting);
  return () => listeners.delete(listener);
}

/**
 * Só é «versão nova» quando havia já um worker a controlar a página. Na primeira instalação não há
 * controller nenhum: avisar aí seria um falso aviso com a app acabada de instalar.
 */
function hasController(serviceWorker) {
  return !!(serviceWorker && serviceWorker.controller);
}

function markWaiting(worker, serviceWorker) {
  if (!worker || !hasController(serviceWorker) || waiting === worker || applied) return;
  waiting = worker;
  notify();
}

/**
 * Verifica se há versão nova: `registration.update()` ao arrancar, quando a app volta ao ecrã
 * (visibilitychange/focus) e de `intervalMs` em `intervalMs` (30 min por omissão).
 * Devolve a função que pára a verificação.
 */
export function startUpdateChecks(reg, options = {}) {
  const {
    intervalMs = UPDATE_CHECK_INTERVAL_MS,
    doc = typeof document !== 'undefined' ? document : null,
    win = typeof window !== 'undefined' ? window : null,
    now = () => Date.now(),
    serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
  } = options;

  if (!reg || typeof reg.update !== 'function') return () => {};

  let lastCheckAt = 0;
  let timer = null;

  const check = (force) => {
    const at = now();
    if (!force && at - lastCheckAt < MIN_MS_BETWEEN_CHECKS) return;
    lastCheckAt = at;
    try {
      const pending = reg.update();
      // Quando a verificação acaba, a versão nova pode já estar em espera: o `statechange` do worker
      // pode ter passado antes de a app se ter ligado a ele, e é esta segunda leitura que o apanha.
      if (pending && typeof pending.then === 'function') {
        pending.then(() => {
          if (reg.waiting) markWaiting(reg.waiting, serviceWorker);
        }, () => {});
      }
    } catch (e) {
      // Sem rede, ou o browser recusou: a verificação seguinte trata disso. Não é erro para o ecrã.
    }
  };

  const onVisibility = () => {
    if (doc && doc.visibilityState === 'visible') check(false);
  };
  const onFocus = () => check(false);

  check(true); // ao arrancar
  timer = setInterval(() => check(true), intervalMs);
  if (doc && doc.addEventListener) doc.addEventListener('visibilitychange', onVisibility);
  if (win && win.addEventListener) win.addEventListener('focus', onFocus);

  return () => {
    if (timer) clearInterval(timer);
    timer = null;
    if (doc && doc.removeEventListener) doc.removeEventListener('visibilitychange', onVisibility);
    if (win && win.removeEventListener) win.removeEventListener('focus', onFocus);
  };
}

/**
 * Liga o registo do service worker ao aviso e à verificação periódica. Chamado uma vez, no arranque
 * (`src/index.js`), depois de `navigator.serviceWorker.register('/sw.js')`.
 */
export function attachServiceWorkerUpdates(reg, options = {}) {
  if (!reg || typeof reg.addEventListener !== 'function') return () => {};
  registration = reg;
  const serviceWorker = options.serviceWorker
    || (typeof navigator !== 'undefined' ? navigator.serviceWorker : null);

  const onUpdateFound = () => {
    const worker = reg.installing;
    if (!worker || typeof worker.addEventListener !== 'function') return;
    worker.addEventListener('statechange', () => {
      // `installed` com um controller vivo = há versão nova à espera (é o sinal que o aviso usa).
      if (worker.state === 'installed') markWaiting(worker, serviceWorker);
    });
  };

  reg.addEventListener('updatefound', onUpdateFound);

  // Já estava uma versão à espera quando a app abriu (instalou numa visita anterior).
  if (reg.waiting) markWaiting(reg.waiting, serviceWorker);

  if (stopChecks) stopChecks();
  stopChecks = startUpdateChecks(reg, options);

  return () => {
    if (typeof reg.removeEventListener === 'function') reg.removeEventListener('updatefound', onUpdateFound);
    if (stopChecks) {
      stopChecks();
      stopChecks = null;
    }
  };
}

/**
 * Aplica a versão nova: pede ao worker em espera que assuma o controlo (`SKIP_WAITING`). Quem
 * recarrega é o UpdateBanner, ao ouvir `controllerchange` — recarregar antes disso voltava a abrir a
 * versão velha. Devolve false quando não havia nada em espera.
 */
export function applyUpdate() {
  const worker = waiting || (registration && registration.waiting);
  if (!worker || typeof worker.postMessage !== 'function' || applied) return false;
  applied = true;
  worker.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

/** Só para testes: esquece registo, worker em espera e avisos entre cenários. */
export function resetUpdateStateForTests() {
  if (stopChecks) {
    stopChecks();
    stopChecks = null;
  }
  registration = null;
  waiting = null;
  applied = false;
  listeners.clear();
}
